import { csrfRequest as request } from "./csrf-request";
import type { Locale } from "./paraglide/runtime";
import type { Currency, Period } from "./products";

export type APIProduct = {
  product_id: string;
  name: string;
  sku: string;
  period: Period;
  currency: Currency;
  price: string;
  trial_days?: number;
};

export type UserSettings = {
  locale?: Locale;
};

export type User = {
  subscriber_email: string;
  subscriber_id: string;
  subscriptions: Array<Subscription>;
  subscriber_settings: UserSettings;
  used_trials: Array<Subscription["product_id"]>;
};

export type Subscription = {
  order_id: string;
  product_id: string;
  subscription_id: string;
  _status?: "trial" | "overdue"; // Temporary means of determining if subscription is overdue or in the trial period. See https://github.com/brave-intl/subscriptions/pull/639
};

export type MatomoParams = {
  mtm_campaign: string;
  mtm_source: string;
  mtm_medium: string;
  mtm_content: string;
};

type ActivationData = {
  can_extend: boolean
};

export const auth = ({
  email,
  locale,
  ux,
  intent,
  product_id,
  product_name,
  matomo_params,
}: {
  email: string;
  locale?: Locale;
  ux?: string;
  intent?: string;
  product_id?: string;
  product_name?: string;
  matomo_params?: MatomoParams;
}): Promise<object> => request("/auth", "POST", { email, ux, intent, product_id, product_name, locale, matomo_params });

export const confirm = (requestId: string, code: string): Promise<User> =>
  request(`/auth/${requestId}`, "PUT", { code });

export const subscribe = (product_id: string, matomo_params?: MatomoParams): Promise<Subscription> =>
  request("/subscriptions", "POST", { product_id, matomo_params }) as Promise<Subscription>;

export const resubscribe = (
  product_id: string,
  subscriptionId: string,
  matomo_params?: MatomoParams
): Promise<Subscription> =>
  request(`/subscriptions/${subscriptionId}`, "POST", {
    product_id,
    matomo_params,
  }) as Promise<Subscription>;

export const cancel = (subscriptionId: string): Promise<object> =>
  request(`/subscriptions/${subscriptionId}`, "DELETE");

export const deleteCredentials = (subscriptionId: string): Promise<object> =>
  request(`/subscriptions/${subscriptionId}/credentials`, "DELETE");

export const getSubscriber = (): Promise<User> => request("/subscribers", "GET");

export const updateLocaleSetting = (subscriberId: string, locale: Locale): Promise<void> =>
  request(`/subscribers/${subscriberId}/settings/locale`, "PATCH", { locale });

export const linkOrderToSubscription = (orderId: string, receipt: string): Promise<User> =>
  request("/subscriptions/receipt", "POST", {
    order_id: orderId,
    receipt: receipt,
  });

export const createAndLinkOrderToSubscription = (receipt: string): Promise<User> =>
  request("/subscriptions/order-receipt", "POST", {
    receipt: receipt,
  });

export const changeEmail = (subscriberId: string, email: string): Promise<object> =>
  request(`/subscribers/${subscriberId}/email`, "PATCH", { emailTo: email });

export const updatePaymentMethod = (subscriptionId: string): Promise<void> =>
  request(`/subscriptions/${subscriptionId}/update-payment-method`, "PATCH").then(res => {
    res.update_payment_method_url && (location.href = res.update_payment_method_url);
  });

export const deleteAccount = (subscriberId: string): Promise<object> =>
  request(`/subscribers/${subscriberId}`, "DELETE");

export const logout = (): Promise<object> => request("/logout", "PUT");

export const checkForAvailableExtensions = (subscriptionId: string): Promise<ActivationData> => request(`/subscriptions/${subscriptionId}/credentials/batches/extend`, "GET") as Promise<ActivationData>;

export const requestActivationExtensions = (subscriptionId: string): Promise<void> => request(`/subscriptions/${subscriptionId}/credentials/batches/extend`, "POST");

export const getAPIProducts = async (): Promise<APIProduct[]> => request("/products", "GET", null, { useCsrf: false });

// This allows the request to be made lazily as early as possible (hooks.client.ts).
let apiProductsPromise: ReturnType<typeof getAPIProducts> | undefined;
export async function getAPIProductsPromise() {
  if (!apiProductsPromise) {
    apiProductsPromise = getAPIProducts().catch((err) => {
      apiProductsPromise = undefined;
      throw err;
    });
  }
  return apiProductsPromise;
}
